import React from 'react';

import { withStyles } from '@material-ui/core/styles';

import { Block } from '@klp/design/Block';
import { Link } from '@klp/design/Link';
import { LocalLoader } from '@klp/design/Loader';
import { KlpStyles } from '@klp/design/Theme';
import { getConfig } from '@klpkonsern/appconfig';
import { UtestaaendeFordringerList } from '../components/UtestaaendeFordringerList';
import { useCurrentUser } from '@klp/auth';
import { useGetUtestaaendeFordringerQuery } from '../services/rtkApi';
import type { IUtestaaendeFordringerRequest } from '../interfaces/IUtestaaendeFordringerRequest';
import { useAppSelector } from '@forvalter/store/hooks';
import { usePortfolio } from '@klp/portfolio/store';
import { contextSelectors } from '@klp/contextSelector';
import { useGetReportsQuery } from '@klp/report/services/rtkApi';

type IUtestaaendeFordringerProps = {
    classes?: any;
};

const styles = () => ({
    container: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        justifyContent: 'space-between',
    },
    title: {
        textTransform: 'uppercase',
    },
});

const userRoleMap: Record<string, string> = {
    isForvalter: 'forvalter',
    isOkonomi: 'oekonomi',
    isDrift: 'drift',
};

const UtestaaendeFordringerSummaryComponent: React.FC<
    IUtestaaendeFordringerProps
> = ({ classes }) => {
    const { currentUser: user } = useCurrentUser();
    const { data: reports = [] } = useGetReportsQuery();
    const { portfolio } = usePortfolio();
    const contextState = useAppSelector(contextSelectors);

    const reportLink = React.useMemo(() => {
        const reportName = getConfig(
            'WEBAPP_UTESTAAENDE_FORDRINGER_REPORT_NAME',
        );
        const match = reports.find((r) => r.name === reportName);
        return match?.url ?? '';
    }, [reports]);

    const request = React.useMemo(() => {
        const req: IUtestaaendeFordringerRequest = {};

        if (user?.isEDAdmin) {
            return req;
        }

        if (portfolio?.brukerId) {
            req.forvalter = portfolio.brukerId;
        }

        if (contextState?.selectedContext?.nummer) {
            req.bygg = contextState.selectedContext.nummer.toString();
        }

        return req;
    }, [user, portfolio, contextState]);

    const { data: utestaaendeFordringer, isLoading } =
        useGetUtestaaendeFordringerQuery(request);

    const filteredReportLink = () => {
        if (!user) return reportLink;

        let filteredLink = reportLink;
        const userRole = Object.entries(userRoleMap).find(([key]) => user[key]);
        if (userRole) {
            filteredLink += `&filter=dim_bygg_avdeling/${userRole[1]}_epost eq ${user.brukerId}`;
        }

        return filteredLink;
    };

    return (
        <>
            <Block marginBottom="bulky">
                <Block
                    translation="frontPage.utestaaendeFordringer.subtitle"
                    marginTop="compact"
                    klpTypographyVariant="xsmall"
                    className={classes?.title}
                    data-cy="UtestaaendeFordringerSummary__frontpageCardSubtitle"
                />
                <LocalLoader isLoading={isLoading}>
                    {utestaaendeFordringer && (
                        <UtestaaendeFordringerList
                            forsinkede={utestaaendeFordringer.forsinkede}
                        />
                    )}
                </LocalLoader>
            </Block>
            <Link
                target="_blank"
                href={reportLink}
                externalLink={true}
                translation="frontPage.utestaaendeFordringer.allReceivables"
            />
            <Block paddingTop="compact">
                <Link
                    target="_blank"
                    href={filteredReportLink()}
                    externalLink={true}
                    translation="frontPage.utestaaendeFordringer.actionButtonText"
                    data-testid="UtestaaendeFordringerSummary__filteredLink"
                />
            </Block>
        </>
    );
};

UtestaaendeFordringerSummaryComponent.displayName = 'UtestaaendeFordringer';

export const UtestaaendeFordringerSummary = withStyles(KlpStyles(styles))(
    UtestaaendeFordringerSummaryComponent,
);
